import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  Autocomplete,
  Chip,
  Tooltip,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { pressFormService } from '../services/api';
import api from '../services/api';

let cgKeySeq = 0;
const nextCgKey = () => { cgKeySeq += 1; return `cg${cgKeySeq}`; };

function PressFormsPage() {
  const [pressForms, setPressForms] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    identifier: '',
    production_time_seconds: '',
    number_of_cavities: '',
    allow_permol: true,
    cycles_before_maintenance: '',
    description: '',
  });
  const [cavityGroups, setCavityGroups] = useState([]);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    loadData();
    api.get('/products/').then((r) => setProducts(r.data?.results ?? r.data ?? [])).catch(() => {});
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await pressFormService.getAll();
      setPressForms(res.data.results || res.data);
    } catch (error) {
      console.error('Помилка завантаження:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (pressForm = null) => {
    setSaveError('');
    if (pressForm) {
      setEditing(pressForm.id);
      setFormData({
        name: pressForm.name,
        identifier: pressForm.identifier,
        production_time_seconds: pressForm.production_time_seconds,
        number_of_cavities: pressForm.number_of_cavities,
        allow_permol: pressForm.allow_permol !== false,
        cycles_before_maintenance: pressForm.cycles_before_maintenance ?? '',
        description: pressForm.description || '',
      });
      // Мерджимо явні групи гнізд + вироби з конфігів (linked_products), яких ще немає в групах
      const explicitGroups = (pressForm.cavity_groups || []).map((g) => ({
        tempKey: nextCgKey(),
        id: g.id,
        product: g.product,
        product_name: g.product_name || '',
        number_of_cavities: String(g.number_of_cavities),
      }));
      const explicitProductIds = new Set(explicitGroups.map((g) => Number(g.product)));
      const linkedExtra = (pressForm.linked_products || [])
        .filter((lp) => !explicitProductIds.has(Number(lp.product)))
        .map((lp) => ({
          tempKey: nextCgKey(),
          id: null,
          product: lp.product,
          product_name: lp.product_name || '',
          number_of_cavities: String(lp.number_of_cavities || ''),
        }));
      setCavityGroups([...explicitGroups, ...linkedExtra]);
    } else {
      setEditing(null);
      setFormData({
        name: '',
        identifier: '',
        production_time_seconds: '',
        number_of_cavities: '',
        allow_permol: true,
        cycles_before_maintenance: '',
        description: '',
      });
      setCavityGroups([]);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setCavityGroups([]);
    setSaveError('');
  };

  const addCavityGroup = () =>
    setCavityGroups((prev) => [...prev, { tempKey: nextCgKey(), product: '', number_of_cavities: '1' }]);

  const removeCavityGroup = (key) =>
    setCavityGroups((prev) => prev.filter((g) => g.tempKey !== key));

  const updateCavityGroup = (key, field, value) =>
    setCavityGroups((prev) => prev.map((g) => g.tempKey === key ? { ...g, [field]: value } : g));

  // сума гнізд по групах
  const cavityGroupsTotal = cavityGroups.reduce((s, g) => s + (parseInt(g.number_of_cavities) || 0), 0);
  const totalCavities = parseInt(formData.number_of_cavities) || 0;
  const cavityMismatch = cavityGroups.length > 0 && cavityGroupsTotal !== totalCavities;

  const handleSubmit = async () => {
    setSaveError('');
    if (cavityMismatch) {
      setSaveError(`Сума гнізд по групах (${cavityGroupsTotal}) не збігається із загальною кількістю гнізд (${totalCavities})`);
      return;
    }
    try {
      const payload = {
        ...formData,
        cycles_before_maintenance: formData.cycles_before_maintenance !== '' ? Number(formData.cycles_before_maintenance) : null,
        cavity_groups: cavityGroups
          .filter((g) => g.product && parseInt(g.number_of_cavities) > 0)
          .map((g) => ({
            product: Number(g.product),
            number_of_cavities: parseInt(g.number_of_cavities),
          })),
      };
      if (editing) {
        await pressFormService.update(editing, payload);
      } else {
        await pressFormService.create(payload);
      }
      handleClose();
      loadData();
    } catch (error) {
      console.error('Помилка збереження:', error);
      const msg = error.response?.data;
      setSaveError(typeof msg === 'string' ? msg : JSON.stringify(msg) || 'Помилка збереження даних');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Ви впевнені, що хочете видалити цю прес-форму?')) {
      try {
        await pressFormService.delete(id);
        loadData();
      } catch (error) {
        console.error('Помилка видалення:', error);
        alert('Помилка видалення');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Прес-форми</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Додати прес-форму
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Ідентифікатор</TableCell>
              <TableCell>Назва</TableCell>
              <TableCell>Час виробництва (сек)</TableCell>
              <TableCell>Кількість гнізд</TableCell>
              <TableCell>Розподіл гнізд</TableCell>
              <TableCell>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pressForms.map((pf) => (
              <TableRow key={pf.id}>
                <TableCell>{pf.identifier}</TableCell>
                <TableCell>{pf.name}</TableCell>
                <TableCell>{pf.production_time_seconds}</TableCell>
                <TableCell>{pf.number_of_cavities}</TableCell>
                <TableCell>
                  {(pf.cavity_groups || []).length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {pf.cavity_groups.map((g) => (
                        <Tooltip key={g.id} title={g.product_name}>
                          <Chip
                            size="small"
                            label={`${g.product_name} × ${g.number_of_cavities}`}
                            variant="outlined"
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(pf)}><EditIcon /></IconButton>
                  <IconButton onClick={() => handleDelete(pf.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Редагувати прес-форму' : 'Додати прес-форму'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Назва"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Ідентифікатор"
              value={formData.identifier}
              onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
              fullWidth
            />
            <TextField
              label="Час виробництва (секунди)"
              type="number"
              value={formData.production_time_seconds}
              onChange={(e) => setFormData({ ...formData, production_time_seconds: e.target.value })}
              fullWidth
            />
            <TextField
              label="Кількість гнізд (загальна)"
              type="number"
              value={formData.number_of_cavities}
              onChange={(e) => setFormData({ ...formData, number_of_cavities: e.target.value })}
              fullWidth
              helperText="Загальна кількість гнізд у прес-формі"
            />
            <TextField
              label="Кількість циклів до планової перевірки"
              type="number"
              value={formData.cycles_before_maintenance}
              onChange={(e) => setFormData({ ...formData, cycles_before_maintenance: e.target.value })}
              fullWidth
              inputProps={{ min: 1, step: 1 }}
              helperText="Залиште порожнім, якщо планова перевірка не потрібна"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.allow_permol}
                  onChange={(e) => setFormData({ ...formData, allow_permol: e.target.checked })}
                />
              }
              label="Дозволити використання перемолу"
            />
            <TextField
              label="Опис"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
            />

            <Divider />

            {/* ── Розподіл гнізд по виробах ── */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Розподіл гнізд по виробах
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Якщо прес-форма за один цикл виробляє кілька різних виробів — вкажіть скільки гнізд
                відведено кожному. Залиште порожнім, якщо всі гнізда роблять один виріб.
              </Typography>

              {cavityGroups.map((g) => (
                <Box key={g.tempKey} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'flex-start' }}>
                  <Autocomplete
                    size="small"
                    sx={{ flex: 1 }}
                    options={products}
                    getOptionLabel={(o) => (typeof o === 'object' ? o.name || '' : (products.find((p) => p.id === o)?.name || ''))}
                    isOptionEqualToValue={(o, v) => o.id === (typeof v === 'object' ? v?.id : v)}
                    value={products.find((p) => p.id === Number(g.product)) || null}
                    onChange={(_, opt) => updateCavityGroup(g.tempKey, 'product', opt ? opt.id : '')}
                    renderInput={(params) => <TextField {...params} label="Виріб" placeholder="Пошук виробу…" />}
                    noOptionsText="Не знайдено"
                  />
                  <TextField
                    size="small"
                    label="Гнізд"
                    type="number"
                    value={g.number_of_cavities}
                    onChange={(e) => updateCavityGroup(g.tempKey, 'number_of_cavities', e.target.value)}
                    inputProps={{ min: 1, step: 1 }}
                    sx={{ width: 90 }}
                  />
                  <IconButton size="small" color="error" onClick={() => removeCavityGroup(g.tempKey)} sx={{ mt: 0.5 }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              <Button size="small" startIcon={<AddIcon />} onClick={addCavityGroup}>
                Додати виріб
              </Button>

              {cavityGroups.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="caption"
                    color={cavityMismatch ? 'error' : 'text.secondary'}
                  >
                    Сума гнізд по групах: {cavityGroupsTotal}
                    {totalCavities > 0 && ` / ${totalCavities} загалом`}
                    {cavityMismatch && ' — має збігатись із загальною кількістю гнізд'}
                  </Typography>
                </Box>
              )}
            </Box>

            {saveError && <Alert severity="error">{saveError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained">Зберегти</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PressFormsPage;
